import React, { useEffect, useState } from 'react';
import { matchPath } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Button, Card } from 'react-bootstrap';
import { CrewOptions, CrewDropdown, FarmOptions } from './utils';
import {
    usePermission, isElasticLoad, isAutomaticLoad,
    isFarmView, isCrewView, isEGView, groupBy,
    isCustomerView
} from '../../helpers/common';
import { analyzeParams, analyzeRecentSummary } from '../../actions/analyze';
import { getPlanTypeList } from '../../actions/farm';
import AnalyzeInfoNotFound from './AnalyzeInfoNotFound';
import AnalyzeInfoOne from './AnalyzeInfoOne';
import AnalyzeInfoTwo from './AnalyzeInfoTwo';
import AnalyzeInfoThree from './AnalyzeInfoThree';
import { BALER_CATEGORYIDS, HARVESTING_CATEGORYIDS } from '../../constants';
import "../../../assets/scss/analyze.scss";
import { NumberLoader } from '../widgets/Loader';
import { SummaryYearAccordian } from './SummaryYearAccordian';
import { getDrawingInstance } from '../map/geo/DrawingControl';

const Analyze = (props) => {

    const dispatch = useDispatch();
    const [state, setState] = useState({
        farmId: null,
        planTypeId: null,
        crewId: null,
        customerId: null,
        egId: null,
        view: null,
        rangeId: null,
        shapeId: null,
        range: null,
        fromDate: null,
        toDate: null,
        crewItem: null,
        regionId: null,
        viewType: "analyze",
        farmStats: [],
        drawing: getDrawingInstance()
    });

    const analyze = useSelector((state) => state.analyze.analyzeParams);
    const search = useSelector((state) => state.analyze.searchParams);
    const planTypeList = useSelector((state) => state.farm.planTypeList);
    const { payload: regionWorkedDate, isRequesting } = useSelector((state) => state.geo.workedDate);
    const isAllPlan = useSelector((state) => state.plan.isSelectAllPlan);
    const equipmentGroupList = useSelector((state) => state.equipmentGroup.equipmentGroupList);
    const mapEgList = useSelector((state) => state.map.egList);
    const regionLists = useSelector((state) => state.geo.regionsList)
    const filteredRegionList = regionLists && regionLists.length > 0 && regionLists.filter(e => e.geo_region_uuid === analyze.regionId).find(data => { return data })
    const regionShapeId = filteredRegionList && filteredRegionList.shapes && filteredRegionList.shapes.length > 0 && filteredRegionList.shapes.map(shape => shape.geo_shape_uuid);
    const regionTypeId = filteredRegionList && filteredRegionList.geo_region_type_id;
    const selectedPlanTypeId = useSelector(state => state.plan.selectedPlanType);

    const findEG = isEGView(analyze.view) && analyze.egId ?
        mapEgList.filter(e => e.egId === analyze.egId) : [];
    const categoryId = (findEG.length > 0) ? findEG[0].categoryId : null;

    const getSearchParams = () => {
        const farmId = analyze.farmId ? analyze.farmId : getMatch().params.farmId;

        /* check the EG have scale equipment or tare truck */
        let showTareLoad = false;
        let currentEG = {}
        if (analyze.egId) {
            currentEG = equipmentGroupList.find((list) => list.equipment_group_uuid === analyze.egId);
            if (currentEG && Object.entries(currentEG).length > 0) {
                const { implement, vehicle } = currentEG;
                if ((implement && implement.equipment_type_id === 9) || (vehicle && vehicle.is_tare_truck)) {
                    showTareLoad = true;
                }
            }
        }

        if (isCrewView(analyze.view) && analyze.crewId) {
            const crewList = equipmentGroupList.filter((e) => e.crew.crew_uuid === analyze.crewId);
            if (crewList && crewList.length > 0) {
                let findEg = crewList.find((e) => (e.implement && e.implement.equipment_type_id === 9) || (e.vehicle && e.vehicle.is_tare_truck));
                if (findEg && Object.entries(findEg).length > 0) {
                    showTareLoad = true;
                }
            }
        }

        let params = {
            farmId,
            planTypeId: analyze.planTypeId,
            crewId: analyze.crewId,
            customerId: analyze.customerId,
            egId: analyze.egId,
            view: analyze.view,
            isTruck: analyze.isTruck,
            isHarvester: isEGView(analyze.view) && HARVESTING_CATEGORYIDS.indexOf(categoryId) >= 0,
            isAutomaticLoad: isElasticLoad(),
            rangeId: search.rangeId,
            range: search.range,
            fromDate: search.custom.from,
            toDate: search.custom.to,
            regionId: regionTypeId === 4 ? regionShapeId[0] : analyze.regionId,
            shapeId: analyze.shapeId,
            showTareLoad
        }

        // If equipment group view
        if (isEGView(params.view) && params.egId) {
            if (!state.crewItem || params.egId !== state.egId) {
                const crews = analyze.crews
                    .filter(e => e.crew_uuid === params.crewId)
                    .sort((a, b) => moment.utc(b.end_date).valueOf() - moment.utc(a.end_date).valueOf());
                params = { ...params, crewItem: (crews.length > 0) ? crews[0] : null };
            }
        } else if (!isEGView(params.view)) {
            params = { ...params, crewItem: null };
        }

        setState({ ...state, ...params });
        if (!analyze.farmId) dispatch(analyzeParams({ farmId }));
    }

    const getMatch = () => {
        return matchPath(window.location.pathname, {
            path: `/home/farm/:farmId/map`,
            exact: false,
            strict: false
        });
    }

    const handleClose = () => {
        dispatch(analyzeParams({ show: false }));
        props.onClose();
    }

    const handlePlanType = (item) => {
        if (item.plan_type_id === analyze.planTypeId) return;
        dispatch(analyzeParams({ planTypeId: item.plan_type_id }));
    }

    const handleSelectOption = (type) => {
        const { farmId, crewId } = state;
        if (type === "WhatIf") props.history.push(`/home/farm/${farmId}/crew/whatif/${crewId}`);
    }

    const handleCrewSelect = (item) => {
        dispatch(analyzeParams({ crewId: item.crew_uuid }));
        setState({ ...state, crewItem: item });
    }

    const setViewType = (type) => {
        setState({ ...state, viewType: type });
    }

    useEffect(() => {
        if (!analyze.show) dispatch(analyzeParams({ show: true }));
        if (planTypeList.length === 0) dispatch(getPlanTypeList());
        getSearchParams();
    }, [analyze.show, planTypeList])

    useEffect(() => {
        if (analyze.view === null) return;
        if (state.farmId === analyze.farmId
            && state.planTypeId === analyze.planTypeId
            && state.crewId === analyze.crewId
            && state.egId === analyze.egId
            && state.view === analyze.view
            && state.rangeId === search.rangeId
            && state.regionId === analyze.regionId)
            return;
        if (!analyze.regionId) {
            setViewType('analyze');
        }

        getSearchParams();
    }, [analyze, search]);

    useEffect(() => {
        return () => {
            dispatch(analyzeRecentSummary({
                replayBox: null,
                workWindow: null
            }));
        }
    }, [])

    const getComponent = () => {
        const s = state;
        if (s.view && s.range && analyze.show) {
            const { crews, egId, title } = analyze;
            const isCrewDropdown = (isEGView(s.view) && egId && crews.length > 0);
            const withLoadTrips = usePermission()("farm", "create") && isAutomaticLoad();

            let Component = null, className = "";
            if (isEGView(s.view) && !s.egId) Component = AnalyzeInfoNotFound;
            else if (isEGView(s.view) && BALER_CATEGORYIDS.indexOf(categoryId) >= 0) {
                Component = AnalyzeInfoThree;
                className = "card-size-baler";
            }
            else if (isEGView(s.view) && s.isTruck) {
                Component = AnalyzeInfoTwo;
                className = "card-size-truck";
                if (withLoadTrips) className += " card-size-with-load-trips";
            }
            else if (isEGView(s.view) && !s.isTruck) {
                Component = AnalyzeInfoOne;
                className = "card-size-nontruck";
            }
            else if (isCrewView(s.view)) {
                Component = AnalyzeInfoOne;
                className = "card-size-crew";
            }
            else if (s.regionId) {
                Component = AnalyzeInfoOne;
                className = "card-size-region";
            }
            else Component = AnalyzeInfoOne;

            const selectedRegionId = analyze.regionId;

            let workedDate = selectedRegionId && regionWorkedDate && regionWorkedDate[selectedRegionId] ?
                regionWorkedDate[selectedRegionId] : [];
            workedDate = workedDate.sort((a, b) => new Date(b.worked_day).valueOf() - new Date(a.worked_day).valueOf())

            let workedWithYears = workedDate.map((e) => {
                return {
                    ...e,
                    workedYear: e.worked_day.toString().split('-')[0]
                }
            });
            workedWithYears = groupBy('workedYear')(workedWithYears);
            const isLogisticPlanType = selectedPlanTypeId === 5;
            return <>
                <Card className={`card-analyze ${className} ${isCustomerView(s.view) ? "card-size-customer" : ""} ${isLogisticPlanType && isCustomerView(s.view) ? "d-none" : ""}`}>
                    <Card.Header>
                        <div className="d-flex justify-content-between title-info">
                            <div className="card-close" title="Close" onClick={handleClose}>
                                <i className="fa fa-times" />
                            </div>
                            <div className="d-flex justify-content-end">
                                <Card.Title as="h6">{title}</Card.Title>
                                {isAllPlan && isFarmView(s.view) && <FarmOptions
                                    options={planTypeList}
                                    onSelect={handlePlanType}
                                    value={s.planTypeId}
                                />}
                                {isCrewView(s.view) && <CrewOptions onSelect={handleSelectOption} />}
                            </div>
                        </div>
                        {isCrewDropdown && s.crewItem &&
                            <div className="d-flex justify-content-end title-sub-info">
                                <CrewDropdown
                                    crews={crews}
                                    selected={s.crewItem}
                                    onSelect={handleCrewSelect}
                                />
                            </div>}
                    </Card.Header>
                    <Card.Body>
                        {selectedRegionId ? <div className='text-center p-3'>
                            <div role="group" className="btn-group-toggle-custom btn-group-toggle btn-group btn-group-sm btn-group-toggle">
                                <button type="button" onClick={() => setViewType("analyze")} className={`${state.viewType === "analyze" ? "btn-primary" : "btn-default"} btn btn-sm px-4`}>Analyze</button>
                                <button type="button" onClick={() => setViewType("summary")} className={`${state.viewType === "summary" ? "btn-primary " : "btn-default"} btn btn-sm px-4`}>Recent Summary</button>
                            </div>
                        </div> : null}

                        {state.viewType === "analyze" && <Component regionTypeId={regionTypeId} params={s} />}
                        {(state.viewType === "summary" && workedDate && workedDate.length > 0 &&
                            !isRequesting && selectedRegionId) ?
                            <>
                                {
                                    Object.keys(workedWithYears).sort((a, b) => b - a).map((year, idx) => {
                                        return (
                                            <SummaryYearAccordian key={`p${idx}`} year={year} data={workedWithYears[year]} index={idx} propState={s} />
                                        )
                                    })
                                }
                            </> :
                            (state.viewType === "summary" && !isRequesting && workedDate.length === 0)
                                ? <div style={{ textAlign: 'center', height: '350px' }}>No records found</div> :
                                (state.viewType === "summary" && isRequesting) ?
                                    <div style={{ textAlign: 'center', height: '350px' }}><NumberLoader /></div> : null

                        }
                        {analyze.egId &&
                            analyze.egId === '110550d0-9999-11ec-b762-42010a8a0045' &&
                            <div style={{ textAlign: 'center', marginTop: '10px' }}>
                                <Button variant='primary'
                                    onClick={() => window.open(`/home/farm/${analyze.farmId}/eg/trip/${analyze.egId}`, "_blank")}
                                ><i className='fa fa-area-chart mx-2' />Trips</Button>
                            </div>}
                    </Card.Body>
                </Card>
            </>;
        }
        else return <></>;
    }

    return getComponent();
}

export default Analyze;